/** @module csi/prefs/PreferenceView */
define([
	"dojo/_base/config",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"lodash",
	"underscore",
	"src/c8-platform/type/customView",
	"csi",
	"csi/middleware/attributes/attributeKeysService",
	"csi/middleware/customView/customViewLoadService",
	"csi/prefs/_base",
	"csi/core/Key",
	"csi/sessionConstants",
	"csi/state/rootStore",
	"csi/types",
	"csi/utility/coreUtility",
	"csi/xml"
], function(config, declare, lang, {find}, _, {CustomViewVersion}, csi,
	{resolveAttributeKeysBasedOnSourceView, validateAttributeKeyMap},
	{attributeKeysLoad, hiddenKeysLoad},
	csiPrefs, Key, sessionConstants, {sessionStore}, types, {isDefined}, xml) {

	// key of all the required custom view options
	const REQUIRED_CUSTOM_VIEW_OPTIONS = new Set(["prefName", "prefParty", "prefDescription", "prefIsShared"]);
	var FILTER_RELATIVE_DATES_OLD = "R";
	var OP_BETWEEN = "BE";
	var OP_NOT_BETWEEN = "NB";

	var FORMER_ABBREVIATIONS = {
		"chartSettings": "TC",
		"filterSettings": "F",
		"groupSettings": "TG",
		"imageSize": "I",
		"reportSettings": "TR",
		"sortSettings": "S",
		"tableFreezeColumns": "TF"
	};

	var DONT_PERSIST_OPTIONS = {
		"attributeKeys": 1,
		"nestedViews": 1,
		"querySize": 1
	};

	var SPLIT_FILTER_VALUE_OPS = {
		"EQ": 1,
		"NE": 1,
		"RE": 1,
		"NM": 1,
		"CO": 1,
		"NC": 1
	};

	var SPLIT_FILTER_VALUE_NOT_OPS = {
		"NE": 1,
		"NM": 1,
		"NC": 1
	};

	function _addCondition(/*Object[]*/conditions, /*C8AttributeType*/valueType, /*String*/key, /*String*/op, /*Any*/value, /*Boolean*/group) {
		// summary: add new condition
		if (_.isString(value)) {
			if (valueType == xml.ATTR_BOOLEAN) {
				value = xml.valueIsStringTrue(value);
			} else if (valueType == xml.ATTR_TIME) {
				value = Number(value);
			}
		}

		conditions.push(csiPrefs.normalizeFilterCondition({
			key,
			op,
			value,
			group,
		}));
	}

	function _getOldFilterValues(/*String*/op, /*Any*/value, /*Boolean*/bNumeric) {
		// summary: handle values that included ranges or multiple values separated by semi-colon
		if (!_.isString(value) || !SPLIT_FILTER_VALUE_OPS[op]) {
			return [value];
		}

		var values = [];

		_.each(value.split(";"), function(v) {
			var a = bNumeric && v.split("-");
			if (a && (a.length == 2)) {
				var n1 = Number(a[0]);
				var n2 = Number(a[1]);

				if (_.isNumber(n1) && _.isNumber(n2) && (n1 <= n2)) {
					for (var i = n1; i <= n2; ++i) {
						values.push(i);
					}
				} else {
					console.error("Could not convert filter value to number range: " + v);
				}
			} else {
				values.push(v.trim());
			}
		});

		return values;
	}

	function _mapFilters(/*Object*/filters, /*Function*/mapKey) {
		// summary: apply map function to filters
		var obj = lang.clone(filters);

		_.each(obj, function(filterObj) {
			filterObj[csiPrefs.FILTER_OR] = !!filterObj[csiPrefs.FILTER_OR];
			filterObj[csiPrefs.FILTER_FLOATING_DATES] = !!filterObj[csiPrefs.FILTER_FLOATING_DATES];
			filterObj[csiPrefs.FILTER_CONDITIONS] = _.filter(filterObj[csiPrefs.FILTER_CONDITIONS], function(c) {
				if (c.conditions) {
					_.forEach(c.conditions, function(condition) {
						if (condition.key = mapKey(condition.key)) {
							if (condition.relative) {
								condition.value = mapKey(condition.value);
							}

							csiPrefs.normalizeFilterCondition(condition);
							return true;
						}

						return false;
					})
					return true;
				} else {
					if (c.key = mapKey(c.key)) {
						if (c.relative) {
							c.value = mapKey(c.value);
						}

						csiPrefs.normalizeFilterCondition(c);
						return true;
					}

					return false;
				}
			});
		});

		return obj;
	}

	function _splitConditionValue(/*Object[]*/conditions, /*C8AttributeType*/valueType, /*Object*/obj, /*String*/value) {
		var bContainer = xml.attributeTypeIsContainer(valueType);
		var bNumeric = xml.attributeTypeIsNumeric(valueType);
		var v = _getOldFilterValues(obj.op, value, bNumeric);
		var nLast = v.length - 1;

		_.each(v, function(v, i) {
			var op = obj.op;
			var bNot = SPLIT_FILTER_VALUE_NOT_OPS[op];

			if (bContainer && (op == xml.OP_EQUALS)) {
				op = "CO";
			} else if (bContainer && (op == xml.OP_NOT_EQUALS)) {
				op = "NC";
			}

			_addCondition(conditions, valueType, obj.key, op, v, !bNot && (i < nLast));
		});
	}

	function _loadOldCondition(/*Object[]*/conditions, /*Object*/obj, /*C8AttributeType*/valueType) {
		// summary: convert old condition object to one or more condition objects
		if (obj.op == OP_BETWEEN) {
			_addCondition(conditions, valueType, obj.key, xml.OP_GREATER_EQUAL, obj.v1, true);
			_addCondition(conditions, valueType, obj.key, xml.OP_LESS_EQUAL, obj.v2);
		} else if (obj.op == OP_NOT_BETWEEN) {
			_addCondition(conditions, valueType, obj.key, xml.OP_LESS_THAN, obj.v1, true);
			_addCondition(conditions, valueType, obj.key, xml.OP_GREATER_THAN, obj.v2);
		} else {
			_splitConditionValue(conditions, valueType, obj, obj.v1);
		}
	}

	function _expandMatchFilterValues(/*Object*/filterObj) {
		// summary: handle missing migration of filter values separated by semi-colons using match operator;
		// this is only supported if filter conditions are combined using AND
		if (filterObj[csiPrefs.FILTER_OR]) {
			return false;
		}

		var oldConditions = filterObj[csiPrefs.FILTER_CONDITIONS];
		var conditions = filterObj[csiPrefs.FILTER_CONDITIONS] = [];
		var bGroup;

		_.each(oldConditions, function(obj) {
			const key = (bGroup || obj.group || obj.relative) ? null : Key.toKey(obj.key);
			if (key) {
				_splitConditionValue(conditions, key.valueType, obj, obj.value);
			} else {
				conditions.push(obj);
			}

			bGroup = obj.group;
		});
	}

	function _migrateDateFilters(/*Object*/filterObj) {
		// summary: migrate EQ and NE date filters
		filterObj[csiPrefs.FILTER_CONDITIONS] = _.filter(filterObj[csiPrefs.FILTER_CONDITIONS], function(obj) {
			const key = Key.toKey(obj.key);
			if (key && types.supportsDateRange(key.valueType, key.valueFormat)) {
				// does not make sense to support not equal to range, so remove
				if (obj.op == xml.OP_NOT_EQUALS) {
					return false;
				}

				// convert equals into range
				if (obj.op == xml.OP_EQUALS) {
					obj.op = xml.OP_RANGE;
					obj.value = types.getDateRange(types.getStartOfDay(obj.value));
				}
			}

			return true;
		});
	}

	return declare(null, {
		/** @property {ICustomViewOption[]} customOptions - array of custom options to save with preferences */
		//customOptions: null,

		/** @property {Object<number,string>} keys - mapping of all keys used in the custom view by index */
		keys: null,

		/** @property {Object<C8NodeType,C8AttributeKey} [mapMatrixTypeToKey] - map of matrix target types to matrix key used in filterSettings */
		mapMatrixTypeToKey: null,

		/** @property {string} name - name of custom view */
		name: null,

		/** @property {PreferenceView} sourceView - source custom view, populated when non-cv editor saves shared view as personal view */
		sourceView: null,

		// String: target class of view which attributes are relative to
		targetClass: null,

		/** @property {number} timestamp - time custom view was last modified */
		timestamp: 0,

		// String: url of _CS_PreferenceView node (unique id)
		urlNode: null,

		// Object: preference data
		values: null,

		/** @property {number} versionOnNode - version of custom view when loaded/saved */
		versionOnNode: null,

		// Boolean: indicates if customOptions have been copied - necessary for addCustomOption so that it does not affect initial value (shared) array
		//_bNewOptions: false,

		constructor: function(/*Object*/args) {
			if (args) {
				lang.mixin(this, args);
			}

			this.name = csiPrefs.getDefaultViewName();

			this.values = csi.hashArray(this.customOptions, "name", "value", {
				prefName: this.name,
				prefParty: [],
				prefDescription: "",
				prefIsShared: true
			});
		},

		newInstance: function() {
			// summary: create new instance of preference object - copy custom options in case
			// they were added dynamically to template
			const {customOptions, keys, mapMatrixTypeToKey, targetClass} = this;

			var obj = new this.constructor({
				keys,
				targetClass,
			});

			// the constructor will add custom options for specific views,
			// so need to overwrite them here with custom options from default view that could have some options removed
			obj.customOptions = customOptions;
			obj.mapMatrixTypeToKey = mapMatrixTypeToKey;

			return obj;
		},

		/**
		 * Add new option to custom view
		 * @param {string} name
		 * @param {any} value
		 * @param {boolean} [isKey]
		 * @param {string} [formerName]
		 * @param {Partial<ICustomViewOption>} [params]
		 */
		addCustomOption: function(name, value, isKey, formerName, params) {
			var opt = Object.assign({}, params, {
				formerName,
				isKey: !!isKey,
				name,
				value,
			});

			this.customizeOptions();
			this.customOptions.push(opt);

			if (this.values) {
				this.values[name] = value;
			}
		},

		removeCustomOption: function(/*String*/name) {
			// summary: remove specified custom option from preferences
			this.customOptions = _.filter(this.customOptions, function(opt) {return opt.name != name;});
			this._bNewOptions = true;

			if (this.values) {
				delete this.values[name];
			}
		},

		/**
		 * Return true if user can access to this view using the given party
		 * @property {cnl[]} party
		 * @return {boolean}
		 */
		canBeAccessedWith(party) {
			if (!this.isShared()) {
				// if this view is personal, given party must be current party to be able to access to it
				return this.isCurrentUser(party);
			} else if (this.isSystemWide()) {
				// if this view is global, anyone can access to it
				return true;
			} else {
				// role based, need to cover every party of the given party

				if (party.length === 0) {
					// if party is empty, then "party.every" would return true, which is not correct, need to return false
					return false;
				}

				return party.every(partyCnl => this.isSharedWithRole(partyCnl));
			}
		},

		/**
		 * Return true if user can edit the custom view
		 * @return {boolean}
		 */
		canEdit: function() {
			if (this.isDefaultView()) {
				return false;
			}

			if (this.isSystemWide() && !csiPrefs.hasAdminAccess()) {
				return false;
			}

			if (this.isSharedWithRoles() && !csiPrefs.hasEditAccess()) {
				return false;
			}

			return csiPrefs.hasPersonalAccess();
		},

		/**
		 * Return true if user can delete the custom view
		 * @return {boolean}
		 */
		canDelete: function() {
			return this.canEdit() && !this.isSharedWithRolesOfAnotherUser();
		},

		/**
		 * compare preference values and return true if they are the same. v2 represents values from dialog and sometimes not all fields are present in the dialog.
		 * In this case, these fields are null in v2 and we should ignore them
		 * @param {Object} v2
		 * @param {boolean} bOptionsOnly - if it is true, only validate on customOptions and ignore Name, Description and Party values
		 * @return {boolean}
		 */
		compare: function(v2, bOptionsOnly) {
			var v1 = this.values;

			if (!bOptionsOnly) {
				if (v1.prefName != v2.prefName) {
					return false;
				}

				if (csiPrefs.hasEditAccess() && !xml.isSameValue(v1.prefParty, v2.prefParty)) {
					return false;
				}

				if (v1.prefDescription != v2.prefDescription) {
					return false;
				}

			}

			if (this.customOptions) {
				// compare all custom options
				return _.every(this.customOptions, function(opt) {
					var x = v1[opt.name];
					var y = v2[opt.name];
					if (x == null) x = opt.value;
					if (y == null) y = opt.value;
					return _.isEqual(x, y);
				}, this);
			}

			return true;
		},

		/**
		 * Convert given values from persisted to runtime values
		 * @param {Object} persistedValues
		 * @param {Object} runtimeValues
		 * @param {ICustomViewLocalStorageOptions} [localStorageParams]
		 * @return {boolean}
		 */
		convertToRuntimeValues: function(persistedValues, runtimeValues, localStorageParams) {
			const {customOptions, sourceView, timestamp, urlNode} = this;

			// only consider values from local storage that originated from this custom view and
			// were created after this custom view was last modified (and have timestamp - v7.1)
			if (localStorageParams && ((localStorageParams.CV !== urlNode) || (localStorageParams.TS <= timestamp))) {
				return false;
			}

			const sourceViewValues = sourceView?.getValues();
			const mapKey = (keyIndex) => this._mapIndexToAttributeKey(keyIndex);

			// keyMap should only exist when Default custom view is saved to local storage and restored; in that case,
			// there is no default "keys" property
			const keyMap = localStorageParams?.KEYS;
			if (keyMap) {
				this.keys = keyMap;
			}

			customOptions.forEach(opt => {
				// if option cannot be personalized, then copy from source view
				if (sourceViewValues && !opt.personalized) {
					runtimeValues[opt.name] = sourceViewValues[opt.name];
					return;
				}

				let v = persistedValues[opt.name];

				// some options have been renamed, so if value for current name does not exist, then try former name
				if ((v == null) && opt.formerName) {
					v = persistedValues[opt.formerName];
				}

				// some options were previously handled explicitly with shortened names, so try now if value still not found
				if ((v == null) && FORMER_ABBREVIATIONS[opt.name]) {
					v = persistedValues[FORMER_ABBREVIATIONS[opt.name]];
				}

				// some options have more than one former name
				if ((v == null) && opt.secondaryName) {
					v = persistedValues[opt.secondaryName];
				}

				if (v != null) {
					// if options points to data point, then convert key index into key
					if (opt.isKey) {
						v = mapKey(v);
					}

					// call custom function to transform loaded value
					let f = "_" + opt.name + "Load";

					if (this[f]) {
						v = this[f](v, persistedValues, mapKey);
					}
				} else if (DONT_PERSIST_OPTIONS[opt.name] && (runtimeValues[opt.name] != null)) {
					// value is dynamically set as other options are loaded; in that case, accept value that is already set
					return;
				}

				runtimeValues[opt.name] = (v == null) ? opt.value : v;
			});

			if (keyMap) {
				this.keys = null;
			}

			return true;
		},

		/**
		 * Create copy of custom view
		 * @return {PreferenceView}
		 */
		copy: function() {
			// be careful not to deep-copy sourceView
			const {sourceView} = this;

			this.sourceView = null;

			// FIX - probably makes more sense to use newInstance with lang.clone instead?
			// Only really need to copy values rather than whole PreferenceView to later compare
			const obj = csi.deepCopy(this);
			this.sourceView = obj.sourceView = sourceView;

			return obj;
		},

		/**
		 * Return copy of existing values
		 * @return {Object}
		 */
		copyValues: function() {
			return csi.deepCopy(this.values);
		},

		customizeOptions: function() {
			// summary: copy options so that they can be customized if not already done
			if (!this._bNewOptions) {
				this._bNewOptions = true;

				if (this.customOptions) {
					this.customOptions = _.clone(this.customOptions);
				} else {
					this.customOptions = [];
				}
			}
		},

		setOptionDefaultValue: function(/*String*/name, /*Any*/defaultValue) {
			// summary: change default value for given custom option
			var index = csi.locate(this.customOptions, function(opt) {
				return opt.name == name;
			});

			console.assert(index >= 0);

			this.customizeOptions();
			this.customOptions[index] = _.extend(_.clone(this.customOptions[index]), {value: defaultValue});

			if (this.values) {
				this.values[name] = defaultValue;
			}
		},

		/**
		 * Return custom view description
		 * @return {string}
		 */
		getDescription: function() {
			return this.values.prefDescription;
		},

		getName: function() {
			return this.name;
		},

		getOptionValue: function(/*Object*/opt) {
			// summary: return value for option
			return this.getOptionValueFromValue(opt, this.values[opt.name]);
		},

		getOptionValueFromValue: function(/*Object*/opt, /*Any*/v) {
			// summary: return value for option given value (rather than the one stored in custom view)
			return (v == null) ? opt.value : v;
		},

		getParty: function() {
			return this.values.prefParty;
		},

		getUrl: function() {
			return this.urlNode;
		},

		getValue: function(/*String*/optionName) {
			// summary: return
			return this.values[optionName];
		},

		getValues: function() {
			// summary: return preference values
			return this.values;
		},

		/**
		 * Return roles that would be removed from view if new party was set
		 * @param {string[]} newParty
		 * @return {string[]}
		 */
		getRemovedRolesFromParty: function(newParty) {
			if (this.isSharedWithRoles()) {
				const {playableRolesStore} = sessionStore;
				const diff = _.difference(this.getParty(), newParty);
				return _.filter(diff, url => playableRolesStore.get(url));
			}

			return [];
		},

		hasOption: function(/*String*/id) {
			// summary: return true if custom view supports given option
			return this.values.hasOwnProperty(id);
		},

		/**
		 * Return true if view is the default configured view
		 * @return {boolean}
		 */
		isDefaultView: function() {
			return this.urlNode == csi.EMPTY_URL;
		},

		/**
		 * Return true if given party could potentially remove permission to the custom view
		 * @param {string[]} newParty - new party selected for the view
		 * @return {boolean}
		 */
		isPartyDowngraded: function(newParty) {
			if (!this.isShared() || this.isSystemWide(newParty)) {
				// if this view is already personal, or the given party is system-wide, then no downgrade
				return false;
			}

			if (this.isSystemWide() || this.isCurrentUser(newParty)) {
				// two downgrade cases:
				// 1. this view is system wide and the given party is not
				// 2. this view is shared (already checked above) and the given party is personal
				return true;
			}

			// both parties are role based, so potential downgrade if any of the roles are not the same
			return !!this.getRemovedRolesFromParty(newParty).length;
		},

		/**
		 * Return true if the given party is the same as the one here
		 * @param {string[]} party
		 */
		isSameParty: function(party) {
			return xml.isSameValue(party, this.getParty());
		},

		/**
		 * Return true if view is shared with site or roles
		 * @return {boolean}
		 */
		isShared: function() {
			return this.values.prefIsShared;
		},

		/**
		 * Return true if view is shared with given role
		 * @param {string} urlRole
		 * @return {boolean}
		 */
		isSharedWithRole: function(urlRole) {
			return _.contains(this.getParty(), urlRole);
		},

		/**
		 * Return true if view is shared with one or more roles
		 * @return {boolean}
		 */
		isSharedWithRoles: function() {
			return this.isShared() && !this.isSystemWide();
		},

		/**
		 * Return true if view is shared with one or more roles that are not available to the current user
		 * @return {boolean}
		 */
		isSharedWithRolesOfAnotherUser: function() {
			const {playableRolesStore} = sessionStore;
			return this.isSharedWithRoles() && _.some(this.getParty(), (url) => !playableRolesStore.get(url));
		},

		/**
		 * Return true if view is shared with site
		 * @param {string[]} [party] - if not provided, then use party stored on custom view
		 * @return {boolean}
		 */
		isSystemWide: function(party) {
			party = party || this.values.prefParty;
			return !party.length;
		},

		/**
		 * Return true if party references the current user (is personal view)
		 * @param {string[]} [party]
		 */
		isCurrentUser: function(party) {
			party = party || this.getParty();
			return party[0] == sessionConstants.userUrl;
		},

		/**
		 * Remove roles from custom view that are not available to the current user
		 */
		removeRolesOfAnotherUser: function() {
			if (this.isSharedWithRoles()) {
				const {playableRolesStore} = sessionStore;
				this.values.prefParty = this.getParty().filter(url => playableRolesStore.get(url));
			}
		},

		/**
		 * Make custom view personal to current user
		 */
		setCurrentUser: function() {
			this.setParty([sessionConstants.userUrl], false);
		},

		/**
		 * Set description for custom view
		 * @param {string} s
		 */
		setDescription: function(s) {
			this.values.prefDescription = s;
		},

		/**
		 * Set name for custom view
		 * @param {string} s
		 */
		setName: function(s) {
			this.values.prefName = this.name = s;
		},

		/**
		 * Set party of the view
		 * @param {cnl[]} party
		 * @param {boolean} isShared
		 */
		setParty: function(party, isShared) {
			this.values.prefParty = party;
			this.values.prefIsShared = isShared;
		},

		setValue: function(/*String*/optionName, /*Any*/value) {
			// summary: set value for option
			this.values[optionName] = value;
			this.name = this.values.prefName;
		},

		setValues: function(/*Object*/values) {
			this.values = values;
			this.name = values.prefName;
		},

		/**
		 * Load custom view settings
		 * @param {Node} node
		 * @param {PreferenceView} [sourceView]
		*/
		load(node, sourceView) {
			const {customOptions, values} = this;
			const optionKeys = customOptions.map((opt) => opt.name);
			const optionKeysSet = new Set(optionKeys);

			this.urlNode = node.$URL;
			this.sourceView = sourceView;
			this.timestamp = node.$PT;
			this.setName(node.$Name);
			this.setDescription(node.Description);
			this.setParty(node.UMEntry, node.IsShared);

			// remove any invalid (improperly formatted) keys
			this.keys = validateAttributeKeyMap(node.Keys);

			// parse JSON to load options
			const options = node.Options ? JSON.parse(node.Options) : {};

			// add option for relative dates if pre-4.9 custom view
			if (node.RelativeDates) {
				options[FILTER_RELATIVE_DATES_OLD] = true;
			}

			// add values for non-persisted options
			options.nestedViews = node.NestedViews;
			options.querySize = node.PageSize;

			// populate attributeKeys from Attributes if not in options (pre-CustomViewVersion.V791)
			if (optionKeysSet.has("attributeKeys") && !options.attributeKeys) {
				options.attributeKeys = node.Attributes;
			}

			this.versionOnNode = options.VR || 0;

			// convert options to runtime values, which mainly includes mapping key indices to keys
			this.convertToRuntimeValues(options, values);

			// cleanup non-existing custom view options in this.values;
			// this.values are the combination of this.customOptions and the required custom view options
			Object.keys(this.values).forEach((key, index) => {
				const optionFoundInModel = optionKeysSet.has(key);

				if (!optionFoundInModel && !REQUIRED_CUSTOM_VIEW_OPTIONS.has(key)) {
					delete this.values[key];
				}
			});

			// if a personalized view, then update attributeKeys and hiddenKeys values to include new attributes in source view
			if (sourceView && values.attributeKeys) {
				const [isDirty, attributeKeys, hiddenKeys] = resolveAttributeKeysBasedOnSourceView(sourceView.values.attributeKeys, sourceView.values.hiddenKeys, values.attributeKeys, values.hiddenKeys);

				if (isDirty) {
					values.attributeKeys = attributeKeys;
					values.hiddenKeys = hiddenKeys;
					this.timestamp = sourceView.timestamp;
					this.keys = sourceView.keys;
				}
			}
		},

		/**
		 * Return attribute key for given index
		 * @param {string} index 
		 * @return {C8AttributeKey}
		 */
		_mapIndexToAttributeKey(index) {
			if (!index) {
				return index;
			}

			const {keys} = this;
			let key = keys[index];

			// look to see if key is an untyped key (used by fixedColumnWidths in V79 and before)
			if (!key && isNaN(index)) {
				key = find(keys, (typedKey) => Key.untypedKey(typedKey) === index);
			}

			return key;
		},

		/**
		 * Return the Key for the given index (starting from 1) into Keys map
		 * @param {string|number} keyIndex
		 * @return {Key}
		 */
		_mapIndexToKey(keyIndex) {
			const key = this._mapIndexToAttributeKey(keyIndex);
			if (!key) {
				return null;
			}

			// Key constructor can throw exception, so only allow it when debugging, otherwise dump error to console as warning
			let keyObj = null;

			if (config.isDebug) {
				keyObj = Key.toKey(key);
			} else {
				try {
					keyObj = Key.toKey(key);
				} catch (e) {
					console.warn("Key error - \'" + key + "\': " + e.message);
					return null;
				}
			}

			return keyObj;
		},

		_attributeKeysLoad: function(value, options, mapKey) {
			// summary: convert persisted value into runtime option value

			// ensure entries in value are all of type string
			if (this.versionOnNode < CustomViewVersion.V791) {
				options.attributeKeys = value.map(String);
			}

			return attributeKeysLoad(options, mapKey, !!this.sourceView);
		},

		_filterSettingsLoad(value, options, mapKey) {
			// summary: convert persisted value into runtime option value
			const {mapMatrixTypeToKey, versionOnNode} = this;

			if (_.isArray(value)) {
				// load pre-5.0 filters - make sure to convert (Not) Between filters to new format
				const oldValue = value;

				value = {};

				_.each(oldValue, (obj) => {
					const key = this._mapIndexToKey(obj.key);
					if (!key) {
						return;
					}

					const {extendedPath, keyRel, matrix, valueType} = key;
					let filterKey = keyRel;

					if (matrix && (keyRel < 0)) {
						// this case only happens when matrix is configured as a path without an id;
						// in this case, assume it applies to primary relationship
						filterKey = 0;
					} else if (mapMatrixTypeToKey && (keyRel === Key.INDEPENDENT_INDEX)) {
						filterKey = mapMatrixTypeToKey[extendedPath?.startType];

						if (!filterKey) {
							return;
						}
					}

					let filterObj = value[filterKey];

					if (!filterObj) {
						filterObj = value[filterKey] = {};
						filterObj[csiPrefs.FILTER_CONDITIONS] = [];
						filterObj[csiPrefs.FILTER_FLOATING_DATES] = options[FILTER_RELATIVE_DATES_OLD] || false;
						filterObj[csiPrefs.FILTER_OR] = false;
					}

					_loadOldCondition(filterObj[csiPrefs.FILTER_CONDITIONS], obj, valueType);
				}, this);
			}

			if (versionOnNode < CustomViewVersion.V2016) {
				// rework filters that reference column matrix group (such as ___Color Matrix::-1) to point to Node Name of target class used by matrix
				value = _mapFilters(value, (index) => this._mapIndexToKey(index));

				// rework semi-colon separated filter values with match condition
				_.each(value, function(filterObj) {
					_expandMatchFilterValues(filterObj);
				});
			} else {
				// if version is up to date, then this is all that needs to be done to load filters
				value = _mapFilters(value, mapKey);
			}

			if (versionOnNode < CustomViewVersion.V2017) {
				// update date EQ filters to use range, and remove NE filters
				_.each(value, function(filterObj) {
					_migrateDateFilters(filterObj);
				});
			}

			return value;
		},

		/**
		 * Convert key indices to keys
		 * @param {string[]} value
		 * @param {Object} options
		 * @param {CustomViewIndexToKeyFunction} mapKey
		 * @return {string[]}
		 */
		_hiddenKeysLoad(value, options, mapKey) {
			return hiddenKeysLoad(options, mapKey);
		},

		/**
		 * Ensure font scale value is valid
		 */
		_printFontScaleLoad(value, options, mapKey) {
			const n = Number(value);

			// a previous defect allowed the font scale to be "" or null, so fix here
			if (isNaN(n) || (n < sessionStore.minimumFontScaling)) {
				return 100;
			}

			return n;
		},

		_sortSettingsLoad: function(value, options, mapKey) {
			// summary: convert persisted value into runtime option value
			return csi.transform(value, (obj) => {
				const key = mapKey(obj.key);
				// convert former name (obj.desc) to new name
				const descending = isDefined(obj.desc) ? obj.desc : obj.descending;
				return key ? {key, descending} : null;
			});
		},
	});
});
